import { ArrowLeft, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
	BillingModeBadge,
	ClientStatusBadge,
} from "@/components/clients/ClientBadges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAddClientUser } from "@/hooks/useAddClientUser";
import { useClient } from "@/hooks/useClient";
import { useClientBilling } from "@/hooks/useClientBilling";
import { useClientUsers } from "@/hooks/useClientUsers";
import { useMe } from "@/hooks/useMe";

interface AddUserDialogProps {
	teamId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const AddUserDialog = (props: AddUserDialogProps) => {
	const { teamId, open, onOpenChange } = props;
	const addUser = useAddClientUser(teamId);

	const [email, setEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");

	const canSubmit =
		email.trim() && firstName.trim() && lastName.trim() && !addUser.isPending;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!canSubmit) return;
		addUser.mutate(
			{
				email: email.trim(),
				firstName: firstName.trim(),
				lastName: lastName.trim(),
			},
			{
				onSuccess: () => {
					setEmail("");
					setFirstName("");
					setLastName("");
					onOpenChange(false);
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add user</DialogTitle>
					<DialogDescription>
						Create a user in this client account. They sign in with their email
						— no invitation required.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="user-email" className="text-clyr-gray-300">
							Email
						</Label>
						<Input
							id="user-email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="bg-clyr-gray-800 border-clyr-gray-700 text-white"
						/>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="user-first" className="text-clyr-gray-300">
								First name
							</Label>
							<Input
								id="user-first"
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								className="bg-clyr-gray-800 border-clyr-gray-700 text-white"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="user-last" className="text-clyr-gray-300">
								Last name
							</Label>
							<Input
								id="user-last"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								className="bg-clyr-gray-800 border-clyr-gray-700 text-white"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="secondary"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit}>
							{addUser.isPending ? "Adding..." : "Add user"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export const ClientDetailPage = () => {
	const { teamId } = useParams<{ teamId: string }>();
	const { data: client, isLoading } = useClient(teamId);
	const { data: users, isLoading: usersLoading } = useClientUsers(teamId);
	const {
		data: billing,
		isLoading: billingLoading,
		isError: billingError,
	} = useClientBilling(teamId);
	const { data: me } = useMe();
	const [addUserOpen, setAddUserOpen] = useState(false);

	// Display-side gate only — the server enforces owner/admin regardless.
	const canManageUsers =
		me?.member.role === "owner" || me?.member.role === "admin";

	return (
		<div className="space-y-4">
			<Link
				to="/clients"
				className="inline-flex items-center gap-1 text-sm text-clyr-gray-400 hover:text-white transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to clients
			</Link>

			{isLoading ? (
				<Skeleton className="h-32 w-full" />
			) : !client ? (
				<div className="text-clyr-gray-500 py-8">Client not found</div>
			) : (
				<>
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-bold text-white">
							{client.name ?? "Unnamed client"}
						</h1>
						<ClientStatusBadge client={client} />
						<BillingModeBadge mode={client.partnerBillingMode} />
					</div>

					<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
						<CardContent className="p-4">
							<div className="grid grid-cols-4 gap-4 text-sm">
								<div>
									<div className="text-xs uppercase text-clyr-gray-400 mb-1">
										Subscription
									</div>
									<div className="text-white capitalize">
										{client.stripeSubscriptionStatus ?? "—"}
									</div>
								</div>
								<div>
									<div className="text-xs uppercase text-clyr-gray-400 mb-1">
										Members
									</div>
									<div className="text-white tabular-nums">
										{client.memberCount.toLocaleString()}
									</div>
								</div>
								<div>
									<div className="text-xs uppercase text-clyr-gray-400 mb-1">
										Created
									</div>
									<div className="text-white">
										{new Date(client.createdAt).toLocaleDateString()}
									</div>
								</div>
								<div>
									<div className="text-xs uppercase text-clyr-gray-400 mb-1">
										ID
									</div>
									<div className="text-clyr-gray-300 font-mono text-xs truncate">
										{client.id}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
						<CardHeader className="p-4 pb-0">
							<CardTitle className="text-white text-base">Billing</CardTitle>
						</CardHeader>
						<CardContent className="p-4">
							{billingLoading ? (
								<Skeleton className="h-12 w-full" />
							) : billingError || !billing ? (
								<div className="text-sm text-clyr-gray-500">
									Billing details unavailable
								</div>
							) : (
								<div className="grid grid-cols-3 gap-4 text-sm">
									<div>
										<div className="text-xs uppercase text-clyr-gray-400 mb-1">
											Billing mode
										</div>
										<BillingModeBadge mode={billing.partnerBillingMode} />
									</div>
									<div>
										<div className="text-xs uppercase text-clyr-gray-400 mb-1">
											Subscription
										</div>
										<div className="text-white capitalize">
											{billing.stripeSubscriptionStatus ?? "—"}
										</div>
									</div>
									<div>
										<div className="text-xs uppercase text-clyr-gray-400 mb-1">
											Status
										</div>
										<ClientStatusBadge client={billing} />
									</div>
								</div>
							)}
						</CardContent>
					</Card>

					<Card className="bg-clyr-gray-800 rounded-lg border-clyr-gray-700">
						<CardHeader className="flex flex-row items-center justify-between space-y-0 p-4">
							<CardTitle className="text-white text-base">Users</CardTitle>
							{canManageUsers && (
								<Button size="sm" onClick={() => setAddUserOpen(true)}>
									<Plus className="h-4 w-4" />
									Add user
								</Button>
							)}
						</CardHeader>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow className="border-clyr-gray-700 text-xs uppercase">
										<TableHead className="text-clyr-gray-400">Name</TableHead>
										<TableHead className="text-clyr-gray-400">Email</TableHead>
										<TableHead className="text-clyr-gray-400">Role</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{usersLoading ? (
										<TableRow>
											<TableCell
												colSpan={3}
												className="py-8 text-center text-clyr-gray-500"
											>
												Loading...
											</TableCell>
										</TableRow>
									) : !users?.length ? (
										<TableRow>
											<TableCell
												colSpan={3}
												className="py-8 text-center text-clyr-gray-500"
											>
												No users
											</TableCell>
										</TableRow>
									) : (
										users.map((user) => (
											<TableRow
												key={user.id}
												className="border-clyr-gray-700/50"
											>
												<TableCell className="text-white">
													{[user.firstName, user.lastName]
														.filter(Boolean)
														.join(" ") || "—"}
												</TableCell>
												<TableCell className="text-clyr-gray-300">
													{user.email}
												</TableCell>
												<TableCell className="text-clyr-gray-400">
													<span className="inline-flex items-center gap-2">
														{user.roleName ?? "—"}
														{user.owner && (
															<Badge className="border-0 bg-clyr-blue-500/10 text-clyr-blue-400">
																Owner
															</Badge>
														)}
													</span>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					{teamId && canManageUsers && (
						<AddUserDialog
							teamId={teamId}
							open={addUserOpen}
							onOpenChange={setAddUserOpen}
						/>
					)}
				</>
			)}
		</div>
	);
};
